import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { praiApi, mapYmydUserToPraiUser } from "services/ymydApi";
import { log, LogLevel } from "utils/LogUtil";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import Button from "components/atoms/Button";
import YmydSignupTemplate from "components/templates/YmydSignupTemplate";

interface LocationState {
  ymydUser?: any; // YMYD 사용자 정보
}

interface SignupFormData {
  email: string;
  password: string;
  passwordConfirm: string;
  name: string;
  phone: string;
  company: string;
  license?: string;
  ymydId?: number;
}

const YmydSignup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const [formData, setFormData] = useState<SignupFormData>({
    email: "",
    password: "",
    passwordConfirm: "",
    name: "",
    phone: "",
    company: "",
    license: "",
    ymydId: undefined,
  });

  const [errors, setErrors] = useState<{
    password?: string;
    passwordConfirm?: string;
  }>({});

  // 약문약답 사용자 정보로 폼 초기화
  useEffect(() => {
    if (state?.ymydUser) {
      const mappedUser = mapYmydUserToPraiUser(state.ymydUser);
      setFormData({
        email: mappedUser.email || "",
        password: "",
        passwordConfirm: "",
        name: mappedUser.name || "",
        phone: mappedUser.phone || "",
        company: mappedUser.company || "",
        license: mappedUser.license || "",
        ymydId: mappedUser.ymydId,
      });
    } else {
      // 약문약답 정보 없이 접근한 경우 로그인 페이지로 리다이렉트
      log(LogLevel.INFO, "약문약답 사용자 정보 없이 페이지 접근");
      navigate("/login");
    }
  }, [state, navigate]);

  const validatePassword = (password: string): boolean => {
    return password.length >= 8;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, password: value }));

    // Clear previous error when user starts typing
    if (errors.password) {
      setErrors((prev) => ({ ...prev, password: undefined }));
    }
  };

  const handlePasswordConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, passwordConfirm: value }));

    // Clear previous error when user starts typing
    if (errors.passwordConfirm) {
      setErrors((prev) => ({ ...prev, passwordConfirm: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { password?: string; passwordConfirm?: string } = {};

    if (!formData.password) {
      newErrors.password = "비밀번호를 입력해주세요.";
    } else if (!validatePassword(formData.password)) {
      newErrors.password = "비밀번호는 최소 8자 이상이어야 합니다.";
    }

    if (!formData.passwordConfirm) {
      newErrors.passwordConfirm = "비밀번호 확인을 입력해주세요.";
    } else if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = "비밀번호가 일치하지 않습니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      WaitingManager.show({ message: "회원가입 처리 중..." });

      // formData.email이 변경되었을 수 있으므로 직접 사용
      const signupData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        company: formData.company,
        license: formData.license,
        ymydId: formData.ymydId,
      };

      const response = await praiApi.signup(signupData);

      // API 응답: error가 0이면 성공
      if (response.error === 0) {
        WaitingManager.hide();
        ConfirmManager.show({
          title: "회원가입 완료",
          body: "회원가입이 완료되었습니다. 로그인 페이지로 이동합니다.",
          buttons: [
            <Button
              key="ok"
              type="Text"
              size="Medium"
              variant="Contained"
              color="Primary"
              style={{ width: "100%" }}
              onClick={() => {
                ConfirmManager.hide();
                navigate("/login");
              }}
            >
              확인
            </Button>
          ],
        });
      } else {
        throw new Error(response.message || "회원가입에 실패했습니다.");
      }
    } catch (error: any) {
      WaitingManager.hide();
      log(LogLevel.ERROR, "회원가입 에러:", error);
      
      ConfirmManager.show({
        title: "오류",
        body: error.response?.data?.message || error.message || "회원가입 중 문제가 발생했습니다.",
        buttons: [
          <Button
            key="ok"
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            style={{ width: "100%" }}
            onClick={() => {
              ConfirmManager.hide();
            }}
          >
            확인
          </Button>
        ],
      });
    }
  };

  const handleCancel = () => {
    ConfirmManager.show({
      title: "확인",
      body: "회원가입을 취소하시겠습니까?",
      buttons: [
        <Button
          key="yes"
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          style={{ width: "100%" }}
          onClick={() => {
            ConfirmManager.hide();
            navigate("/login");
          }}
        >
          예
        </Button>,
        <Button
          key="no"
          type="Text"
          size="Medium"
          variant="Outlined"
          color="Secondary"
          style={{ width: "100%" }}
          onClick={() => {
            ConfirmManager.hide();
          }}
        >
          아니오
        </Button>
      ],
    });
  };

  return (
    <YmydSignupTemplate
      formData={formData}
      passwordError={errors.password}
      passwordConfirmError={errors.passwordConfirm}
      onPasswordChange={handlePasswordChange}
      onPasswordConfirmChange={handlePasswordConfirmChange}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};

export default YmydSignup;